import type { ComponentProps, FunctionComponent } from 'react'
import {
  provideCardPlatform,
  type ProvideCardPlatformOptions,
} from '@/src/experiences/card-platform/provideCardPlatform'
import { LegacyPaymentMethods } from '@/src/experiences/card-payments/providePaymentMethods/LegacyPaymentMethods'
import { PlatformPaymentMethods } from '@/src/experiences/card-payments/providePaymentMethods/PlatformPaymentMethods'
import * as MesaLib from '@acre-rewards/lib'
import { z } from 'zod/v4'
import type { LegacyCompletePlaidLinkProps } from '@/src/common/hooks/usePlaidLink/useCompletePlaidLink/useLegacyCompletePlaidLink'

/** @todo /@deprecate this in favor just using {@link MesaLib.Plaid.Accounts.PaymentMethod}, which has fine-grained type-safety/precision over what exists under which scenarios and respects data model hierarchies */
export const BasePaymentMethod = z.object({
  name: MesaLib.Plaid.Accounts.PaymentMethod.shape.name,
  last4: z.templateLiteral([z.string(), ' - ', MesaLib.Plaid.Accounts.PaymentMethod.shape.mask]),
  key: z.string(),
  isVerified: z.boolean(),
  products: z.array(MesaLib.Plaid.Products),
  status: MesaLib.Plaid.Accounts.PaymentMethod.shape.item.shape.status,
  plaidAccountId: MesaLib.Plaid.Accounts.PaymentMethod.shape.id.optional(),
  highnoteExternalAccountId: z.string().optional(),
  profileId: MesaLib.PaymentProfile.shape.id.optional(),
})

export type BasePaymentMethod = z.infer<typeof BasePaymentMethod>

export type PaymentMethodsComponent = (props: {
  data?: BasePaymentMethod[]
  loading: boolean
  /** @todo {@link https://linear.app/mesamember/issue/MESA-2130/deleteunlink-payment-profiles} */
  closePaymentMethod?: (paymentMethod: BasePaymentMethod) => void
  closingPaymentMethod?: boolean
  openPlaidLink?: (paymentMethod?: BasePaymentMethod) => void | Promise<void>
  plaidLinkLoading?: boolean
}) => ReturnType<FunctionComponent<typeof props>>

export type PaymentMethods = ComponentProps<PaymentMethodsComponent>

interface ProvidePaymentMethodsConfigs
  extends Pick<ProvideCardPlatformOptions, 'allowNonCardMember'>,
    LegacyCompletePlaidLinkProps {
  products: typeof MesaLib.Plaid.Products.options
}

export function providePaymentMethods({
  allowNonCardMember,
  ...configs
}: ProvidePaymentMethodsConfigs) {
  return provideCardPlatform<PaymentMethodsComponent>(
    ({ ToWrap, platform, newestApplicationQuery }) => {
      switch (platform) {
        case 'HIGHNOTE':
          return <LegacyPaymentMethods ToWrap={ToWrap} {...configs} />
        case 'LOANPRO':
          return <PlatformPaymentMethods ToWrap={ToWrap} {...configs} />
        default:
          platform satisfies undefined
          return !allowNonCardMember ||
            newestApplicationQuery.isUninitialized ||
            newestApplicationQuery.isLoading ? (
            <ToWrap loading />
          ) : (
            <LegacyPaymentMethods ToWrap={ToWrap} {...configs} shouldLinkToHighnote={false} />
          )
      }
    },
    { allowNonCardMember },
  )
}
